import { useRef, useEffect, useState } from 'react';
import './SpinAndWin.css';
import CasinoChip from './CasinoChip';
import { InfoDrawer } from './InfoDrawer';
import { TourModule } from './TourModule';
import JackpotWheel from './JackpotWheel';
import MobileJackpotSpin from './MobileJackpotSpin';
import { ResultModal } from '../../components/ResultModal';
import Axios from '../../Axios';
import { useWallet } from '../../contexts/walletContext';
import {
    SEGMENTS, EMPTY_BETS, COLORS, PRIZE_ACCENT,
    NUM_SEG, SEG_DEG, DEG2RAD,
    LEFT_BETS, RIGHT_BETS, COINS,
} from './constants';
// SEGMENTS, NUM_SEG, SEG_DEG, LEFT_BETS, RIGHT_BETS used only as DEFAULT_WHEEL_CONFIG fallback

const SPEEDS = {
    fast: { label: 'Fast', duration: 2000 },
    normal: { label: 'Normal', duration: 4000 },
    slow: { label: 'Slow', duration: 7000 },
};
const AUTOPLAY_OPTIONS = [5, 20, 25, 50];

// â”€â”€ Helper â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function normalizedPointerRotation(rotation) {
    return ((360 - (rotation % 360)) + 360) % 360;
}

function segAtPointer(rotation, segDeg, numSeg) {
    const normalized = (normalizedPointerRotation(rotation) + 360) % 360;
    return Math.floor(normalized / segDeg) % numSeg;
}

const BET_BOX_COLORS = [
    { grad: 'linear-gradient(180deg,#EEE050,#C8A800)', dark: '#907000' },
    { grad: 'linear-gradient(180deg,#A870E0,#6A30B8)', dark: '#4A1890' },
    { grad: 'linear-gradient(180deg,#E87040,#C03810)', dark: '#8A2800' },
    { grad: 'linear-gradient(180deg,#50D870,#18943A)', dark: '#0D6228' },
    { grad: 'linear-gradient(180deg,#E070CC,#A83898)', dark: '#782068' },
    { grad: 'linear-gradient(180deg,#C8D0D8,#8090A0)', dark: '#506070' },
];

function formatMultiplierLabel(value) {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return String(value) + 'X';
    return (Number.isInteger(numericValue) ? numericValue.toFixed(0) : numericValue.toFixed(1)) + 'X';
}

function normalizeSpinLabel(isFreeSpin, value) {
    if (value === 'jackpot') return 'Jackpot';
    if (isFreeSpin) return `${value} spin`;
    if (value === '0') return '0';
    return formatMultiplierLabel(value);
}

function buildSpacedSegments(labelCounts, minGap) {
    const total = Object.values(labelCounts).reduce((s, c) => s + c, 0);
    const result = [];
    const counts = { ...labelCounts };
    const lastUsed = {};

    for (let i = 0; i < total; i++) {
        const available = Object.entries(counts)
            .filter(([label, count]) => count > 0 && (lastUsed[label] === undefined || i - lastUsed[label] > minGap))
            .sort((a, b) => b[1] - a[1]);

        const pick = available.length > 0
            ? available[0]
            : Object.entries(counts).find(([, c]) => c > 0);

        if (!pick) break;
        const [label] = pick;
        result.push(label);
        counts[label]--;
        lastUsed[label] = i;
    }
    return result;
}

function buildWheelConfig(spinSettings) {
    const labelCounts = {};
    const betLabels = [];

    spinSettings.forEach(s => {
        if (!s.isVisible) return;
        const label = normalizeSpinLabel(s.isFreeSpin, s.value);
        const count = Number(s.show_counts || 0);
        if (count > 0) labelCounts[label] = (labelCounts[label] || 0) + count;

        // Multipliers should be bettable even if isWin: true (so the wheel can land on them)
        // Only exclude free spins, the "0" segment, and the Jackpot segment from the betting panel
        if (!s.isFreeSpin && s.value !== '0' && String(s.value).toLowerCase() !== 'jackpot') {
            if (!betLabels.includes(label)) betLabels.push(label);
        }
    });

    const total = Object.values(labelCounts).reduce((s, c) => s + c, 0);
    if (total === 0) return null;

    const segments = buildSpacedSegments(labelCounts, 2);
    const numSeg = segments.length;
    const segDeg = 360 / numSeg;
    const half = Math.ceil(betLabels.length / 2);
    const leftBets = betLabels.slice(0, half).map((label, i) => ({
        label, ...BET_BOX_COLORS[i % BET_BOX_COLORS.length],
    }));
    const rightBets = betLabels.slice(half).map((label, i) => ({
        label, ...BET_BOX_COLORS[(i + half) % BET_BOX_COLORS.length],
    }));
    const emptyBets = Object.fromEntries(betLabels.map(l => [l, {}]));

    const winLabels = new Set(
        spinSettings
            .filter(s => s.isVisible && s.isWin)
            .map(s => normalizeSpinLabel(s.isFreeSpin, s.value))
    );

    // Segments explicitly marked isWin:false that are NOT zero/free-spin/jackpot
    // These should be avoided during the wheel's landing calculation
    const winDisabledLabels = new Set(
        spinSettings
            .filter(s => s.isVisible && s.isWin === false && !s.isFreeSpin && s.value !== '0' && String(s.value).toLowerCase() !== 'jackpot')
            .map(s => normalizeSpinLabel(s.isFreeSpin, s.value))
    );

    return { segments, numSeg, segDeg, leftBets, rightBets, emptyBets, winLabels, winDisabledLabels };
}

const DEFAULT_WHEEL_CONFIG = {
    segments: SEGMENTS,
    numSeg: NUM_SEG,
    segDeg: SEG_DEG,
    leftBets: LEFT_BETS,
    rightBets: RIGHT_BETS,
    emptyBets: EMPTY_BETS(),
    winLabels: new Set(['Jackpot']),
};

const JACKPOT_LABEL_COLORS = {
    'Nothing': { color: '#fde047', textColor: '#78350f' },
    'Try Again': { color: '#fde047', textColor: '#78350f' },
};

const JACKPOT_WIN_PALETTE = [
    { color: '#881337', textColor: '#fef08a' },
    { color: '#fde047', textColor: '#450a0a' },
    { color: '#7f1d1d', textColor: '#fef08a' },
    { color: '#facc15', textColor: '#450a0a' },
    { color: '#991b1b', textColor: '#fef08a' },
];

function buildJackpotSegments(spinSettings) {
    const visible = spinSettings.filter(s => s.isVisible);
    if (visible.length === 0) return null;

    const positivePercs = visible.map(s => Number(s.perc)).filter(p => p > 0);
    const unit = positivePercs.length > 0 ? Math.min(...positivePercs) : 5;

    const labelCounts = {};
    visible.forEach(s => {
        const explicit = Number(s.show_counts);
        const count = explicit > 0 ? explicit : Math.round(Number(s.perc) / unit);
        if (count > 0) labelCounts[s.label] = (labelCounts[s.label] || 0) + count;
    });

    if (Object.keys(labelCounts).length === 0) return null;

    const spaced = buildSpacedSegments(labelCounts, 2);

    const winLabelColors = {};
    [...visible]
        .filter(s => s.isWin)
        .sort((a, b) => Number(b.perc) - Number(a.perc))
        .forEach((s, i) => {
            winLabelColors[s.label] = JACKPOT_WIN_PALETTE[i % JACKPOT_WIN_PALETTE.length];
        });

    const labelToPct = {};
    visible.forEach(s => { labelToPct[s.label] = s.isWin ? Number(s.perc) : 0; });

    const labelIsWin = {};
    visible.forEach(s => { labelIsWin[s.label] = !!s.isWin; });

    return spaced.map(label => ({
        label,
        pct: labelToPct[label] ?? 0,
        isWin: labelIsWin[label] ?? false,
        ...(JACKPOT_LABEL_COLORS[label] ?? winLabelColors[label] ?? JACKPOT_WIN_PALETTE[0]),
    }));
}

// â”€â”€ Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export default function SpinWheelPage() {
    const canvasRef = useRef(null);
    const autoplayTimeoutRef = useRef(null);
    const lossModalTimeoutRef = useRef(null);
    const currentSpinSourceRef = useRef(null);
    const completedSpinSourceRef = useRef(null);
    const freeSpinsLeftRef = useRef(0);
    const freeSpinTotalWinRef = useRef(0);
    const freeSpinBetRef = useRef(EMPTY_BETS());
    const isFreeSpinPayoutDoneRef = useRef(false);
    const autoplayRemainingRef = useRef(0);
    const isAutoplayActiveRef = useRef(false);
    const lastProcessedAutoplaySpinIdRef = useRef(0);
    const betsRef = useRef(EMPTY_BETS());
    const balanceRef = useRef(50000.00);
    const totalStakeRef = useRef(0);
    const hasBetRef = useRef(false);
    const activeSpinBetsRef = useRef(null);

    // Physics lives in a ref so the RAF loop never stales
    const S = useRef({
        rotation: 0,
        velocity: 0,
        isSpinning: false,
        spinStartTime: 0,
        lastSeg: 0,
        winSeg: null,
        pDeflect: 3.5,
        pVel: 0,
        glowPhase: 0,
        spinDuration: SPEEDS.normal.duration,
    });

    const audioCtxRef = useRef(null);
    const setSpinningRef = useRef(null);
    const setFlashRef = useRef(null);
    const triggerSpinRef = useRef(null);

    const [wheelConfig, setWheelConfig] = useState(DEFAULT_WHEEL_CONFIG);
    const wheelConfigRef = useRef(DEFAULT_WHEEL_CONFIG);
    wheelConfigRef.current = wheelConfig;

    const makeEmptyBets = () =>
        Object.fromEntries(Object.keys(wheelConfigRef.current.emptyBets).map(k => [k, {}]));

    const [result, setResult] = useState(null);
    const [spinning, setSpinning] = useState(false);
    const [flash, setFlash] = useState(false);
    const [balance, setBalance] = useState();
    const { wallet: globalWallet, setWallet: setGlobalWallet } = useWallet() || {};
    const [wallet, setWallet] = useState(globalWallet || { balance: 0 });
    const [spinAndWinLimit, setSpinAndWinLimit] = useState(null);
    const spinAndWinLimitRef = useRef(null);
    spinAndWinLimitRef.current = spinAndWinLimit;

    const [bets, setBets] = useState(EMPTY_BETS());
    const [lastBet, setLastBet] = useState(null);
    const [freeSpinsLeft, setFreeSpinsLeft] = useState(0);
    const [freeSpinTotalWin, setFreeSpinTotalWin] = useState(0);
    const [selectedCoin, setSelectedCoin] = useState(1);
    const [showCoinPicker, setShowCoinPicker] = useState(false);
    const [alertMsg, setAlertMsg] = useState(null);
    const [winModal, setWinModal] = useState(null); // { win, label, amount }
    const [isResultPending, setIsResultPending] = useState(false);
    const [showInfoDrawer, setShowInfoDrawer] = useState(false);
    const [isTourOpen, setIsTourOpen] = useState(false);
    const [isAutoplayModalOpen, setIsAutoplayModalOpen] = useState(false);
    const [autoplayCount, setAutoplayCount] = useState(0);
    const [isAutoplayActive, setIsAutoplayActive] = useState(false);
    const [selectedSpeed, setSelectedSpeed] = useState('normal');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [completedSpinId, setCompletedSpinId] = useState(0);
    const [lockedBets, setLockedBets] = useState(null);
    const [gameSettings, setGameSettings] = useState(null);
    const [isSoundEnabled, setIsSoundEnabled] = useState(true);
    const isSoundEnabledRef = useRef(true);
    isSoundEnabledRef.current = isSoundEnabled;
    const currentSpinIsRewardRef = useRef(false);

    const [showJackpotWheel, setShowJackpotWheel] = useState(false);
    const [jackpotPool, setJackpotPool] = useState(0);
    const [jackpotSegments, setJackpotSegments] = useState(null);
    const [isFreeSpinMode, setIsFreeSpinMode] = useState(false);

    const hasLoadedInitialTourRef = useRef(false);

    useEffect(() => {
        if (typeof wallet?.spinAndWinTutorialStatus === 'boolean' && !hasLoadedInitialTourRef.current) {
            if (wallet.spinAndWinTutorialStatus === false) {
                setIsTourOpen(true);
            }
            hasLoadedInitialTourRef.current = true;
        }
    }, [wallet]);

    setSpinningRef.current = setSpinning;
    setFlashRef.current = setFlash;

    const totalStake = Object.values(bets).reduce((sum, box) => {
        return sum + Object.entries(box).reduce((s, [coin, count]) => {
            return s + Number(coin) * count;
        }, 0);
    }, 0);
    const hasBet = totalStake > 0;
    const hasPreviousBet = lastBet !== null &&
        Object.values(lastBet.bets || {}).some(box =>
            Object.values(box).some(count => count > 0)
        );
    const isFreeSpinSessionActive =
        freeSpinsLeft > 0 ||
        winModal?.mode === 'free-spin-entry' ||
        winModal?.mode === 'free-spin-summary';
    const jackpotTickets = Number(wallet?.spin_and_win?.jackpotTicketCount ?? 0);
    const canOpenJackpotWheel = jackpotTickets >= 1;
    const interactionsLocked =
        spinning ||
        isAutoplayActive ||
        isResultPending;

    betsRef.current = bets;
    balanceRef.current = balance;
    totalStakeRef.current = totalStake;
    hasBetRef.current = hasBet;
    freeSpinsLeftRef.current = freeSpinsLeft;
    freeSpinTotalWinRef.current = freeSpinTotalWin;
    autoplayRemainingRef.current = autoplayCount;
    isAutoplayActiveRef.current = isAutoplayActive;

    const isWinningSegment = (index) => {
        const label = wheelConfigRef.current.segments[index];
        if (!label) return false;

        // Skip only if explicitly marked as isWin: false in admin
        const isWinDisabled = wheelConfigRef.current.winDisabledLabels?.has(label) ?? false;
        if (isWinDisabled) return true;

        const limitData = spinAndWinLimitRef.current;

        // If it's a free spin segment, check the daily free spin limit
        if (label.includes('spin')) {
            return (limitData?.currentFreeSpinCount ?? 0) >= (limitData?.dailyFreeSpinLimit ?? Infinity);
        }

        // Avoid landing on Jackpot again if already won today
        if (label.toLowerCase() === 'jackpot') {
            return limitData?.jackpotClaimedToday === true;
        }

        return false;
    };

    function calculateBetWinAmount(resultLabel, betSnapshot) {
        if (!/x/i.test(resultLabel || '')) return 0;

        const multiplier = parseInt(resultLabel.replace(/x/gi, ''), 10);
        const box = betSnapshot?.[resultLabel] || {};

        return Object.entries(box).reduce((sum, [coin, count]) => {
            return sum + Number(coin) * count * multiplier;
        }, 0);
    }

    function cloneBetState(sourceBets = EMPTY_BETS()) {
        return Object.fromEntries(
            Object.entries(sourceBets).map(([label, box]) => [
                label,
                { ...(box || {}) },
            ])
        );
    }

    function extractSpinWinAmount(apiData, fallbackAmount = 0) {
        const candidates = [
            apiData?.rewardAmount,
            apiData?.win_amount,
            apiData?.won_amount,
            apiData?.payout,
            apiData?.amount_won,
            apiData?.received_amount,
            apiData?.result?.win_amount,
            apiData?.result?.won_amount,
            apiData?.result?.payout,
            apiData?.result?.amount_won,
            apiData?.result?.received_amount,
            apiData?.data?.win_amount,
            apiData?.data?.won_amount,
            apiData?.data?.payout,
            apiData?.data?.amount_won,
            apiData?.data?.received_amount,
        ];

        const resolved = candidates.find(value => Number.isFinite(Number(value)));
        return resolved !== undefined ? Number(resolved) : fallbackAmount;
    }

    function openFreeSpinSummary(totalWinAmount) {
        setWinModal({
            win: totalWinAmount > 0,
            label: 'Free Spin Summary',
            amount: totalWinAmount,
            title: 'Free spins finished',
            subtitle: 'Total won from free spins',
            continueText: 'Continue',
            pendingFreeSpins: null,
            autoClose: false,
            mode: 'free-spin-summary',
        });
        setIsResultPending(true);
    }

    function updateFreeSpinTotalWin(nextValue) {
        freeSpinTotalWinRef.current = nextValue;
        setFreeSpinTotalWin(nextValue);
    }

    // â”€â”€ Audio tick â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    function playTick(velocity) {
        if (!isSoundEnabledRef.current) return;
        try {
            const ac = audioCtxRef.current;
            if (!ac) return;
            if (ac.state === 'suspended') ac.resume();
            const t = ac.currentTime;

            const bufSize = ac.sampleRate * 0.03;
            const buffer = ac.createBuffer(1, bufSize, ac.sampleRate);
            const data = buffer.getChannelData(0);
            for (let i = 0; i < bufSize; i++) {
                data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufSize, 6);
            }
            const noise = ac.createBufferSource();
            noise.buffer = buffer;

            const filter = ac.createBiquadFilter();
            filter.type = 'bandpass';
            filter.frequency.value = Math.max(800, Math.min(3200, 800 + velocity * 60));
            filter.Q.value = 2.5;

            const gain = ac.createGain();
            const vol = Math.min(0.6, 0.15 + velocity * 0.012);
            gain.gain.setValueAtTime(vol, t);
            gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.04);

            noise.connect(filter);
            filter.connect(gain);
            gain.connect(ac.destination);
            noise.start(t);
            noise.stop(t + 0.05);
        } catch (_) { }
    }

    // â”€â”€ Win jingle â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    function playWin(label) {
        if (!isSoundEnabledRef.current) return;
        try {
            const ac = audioCtxRef.current;
            if (!ac) return;
            if (ac.state === 'suspended') ac.resume();

            const isBig = label === '4 spin' || label === 'x40' || label === 'x20';
            const notes = isBig ? [523, 659, 784, 880, 1047, 1319] : [523, 659, 784, 1047];

            notes.forEach((freq, i) => {
                const osc = ac.createOscillator();
                const gain = ac.createGain();
                osc.connect(gain);
                gain.connect(ac.destination);
                const t = ac.currentTime + i * 0.10;

                osc.type = i % 2 === 0 ? 'sine' : 'triangle';
                osc.frequency.setValueAtTime(freq, t);
                osc.frequency.exponentialRampToValueAtTime(freq * 1.01, t + 0.15);

                gain.gain.setValueAtTime(0, t);
                gain.gain.linearRampToValueAtTime(0.3, t + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);

                osc.start(t);
                osc.stop(t + 0.32);
            });

            const endT = ac.currentTime + notes.length * 0.10 + 0.1;
            const bufSize = ac.sampleRate * 0.18;
            const buffer = ac.createBuffer(1, bufSize, ac.sampleRate);
            const data = buffer.getChannelData(0);
            for (let i = 0; i < bufSize; i++) {
                data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufSize, 2);
            }
            const shimmer = ac.createBufferSource();
            shimmer.buffer = buffer;
            const hpf = ac.createBiquadFilter();
            hpf.type = 'highpass';
            hpf.frequency.value = 6000;
            const shimGain = ac.createGain();
            shimGain.gain.setValueAtTime(0.18, endT);
            shimGain.gain.exponentialRampToValueAtTime(0.0001, endT + 0.18);
            shimmer.connect(hpf);
            hpf.connect(shimGain);
            shimGain.connect(ac.destination);
            shimmer.start(endT);
            shimmer.stop(endT + 0.2);
        } catch (_) { }
    }

    function clearAutoplayTimeout() {
        if (autoplayTimeoutRef.current) {
            clearTimeout(autoplayTimeoutRef.current);
            autoplayTimeoutRef.current = null;
        }
    }

    function clearLossModalTimeout() {
        if (lossModalTimeoutRef.current) {
            clearTimeout(lossModalTimeoutRef.current);
            lossModalTimeoutRef.current = null;
        }
    }

    function finalizeSpin() {
        const s = S.current;
        s.velocity = 0;
        s.isSpinning = false;
        const { segDeg: fSegDeg, numSeg: fNumSeg, segments: fSegs } = wheelConfigRef.current;
        const win = segAtPointer(s.rotation, fSegDeg, fNumSeg);
        s.winSeg = win;
        s.glowPhase = 0;
        const label = fSegs[win];
        setIsResultPending(true);
        setResult(label);
        setSpinningRef.current(false);
        playWin(label);
        if (!label.includes('spin')) {
            setFlashRef.current(true);
            setTimeout(() => setFlashRef.current(false), 700);
        }
        console.debug('[SpinAndWin] finalizeSpin', {
            rotation: s.rotation,
            normalizedRotation: normalizedPointerRotation(s.rotation),
            segmentIndex: win,
            label,
        });
        completedSpinSourceRef.current = currentSpinSourceRef.current ?? 'manual';
        currentSpinSourceRef.current = null;
        setCompletedSpinId(previous => previous + 1);
        return label;
    }

    // â”€â”€ Canvas draw + RAF loop â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    useEffect(() => {
        const canvas = canvasRef.current;
        let rafId;

        function setupCanvas() {
            const dpr = window.devicePixelRatio || 1;
            const cont = canvas.parentElement;
            const size = Math.min(cont.clientWidth, cont.clientHeight);
            canvas.style.width = size + 'px';
            canvas.style.height = size + 'px';
            canvas.width = size * dpr;
            canvas.height = size * dpr;
            const ctx = canvas.getContext('2d');
            ctx.scale(dpr, dpr);
        }

        function draw(customRotation) {
            const s = S.current;
            const dpr = window.devicePixelRatio || 1;
            const W = canvas.width / dpr;
            const H = canvas.height / dpr;
            const ctx = canvas.getContext('2d');

            ctx.clearRect(0, 0, W, H);

            const cx = W / 2;
            const cy = H / 2;
            const outerR = W * 0.435;
            const rimW = W * 0.032;
            const segR = outerR - rimW;
            const hubR = W * 0.074;
            const rot = (customRotation ?? s.rotation) * DEG2RAD;
            const wCfg = wheelConfigRef.current;

            // Dark border ring
            ctx.save();
            ctx.shadowColor = 'rgba(0,0,0,0.7)';
            ctx.shadowBlur = 22;
            ctx.shadowOffsetY = 5;
            ctx.beginPath();
            ctx.arc(cx, cy, outerR + 6, 0, Math.PI * 2);
            ctx.fillStyle = '#1A0C00';
            ctx.fill();
            ctx.restore();

            // Gold rim
            ctx.beginPath();
            ctx.arc(cx, cy, outerR + 3, 0, Math.PI * 2);
            ctx.fillStyle = '#C49000';
            ctx.fill();

            const rimGrad = ctx.createLinearGradient(cx - outerR, cy - outerR, cx + outerR, cy + outerR);
            rimGrad.addColorStop(0.00, '#FFF4A0');
            rimGrad.addColorStop(0.20, '#FFD700');
            rimGrad.addColorStop(0.50, '#DAA520');
            rimGrad.addColorStop(0.80, '#C49000');
            rimGrad.addColorStop(1.00, '#A07800');
            ctx.beginPath();
            ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
            ctx.fillStyle = rimGrad;
            ctx.fill();

            ctx.beginPath();
            ctx.arc(cx, cy, segR + 3, 0, Math.PI * 2);
            ctx.lineWidth = 5;
            ctx.strokeStyle = '#1A0C00';
            ctx.stroke();

            ctx.beginPath();
            ctx.arc(cx, cy, segR + 1, 0, Math.PI * 2);
            ctx.lineWidth = 2;
            ctx.strokeStyle = '#FFD700';
            ctx.stroke();

            // Segments
            const winSeg = s.winSeg;
            const glowAlpha = winSeg !== null && !s.isSpinning
                ? 0.22 + 0.16 * Math.sin(s.glowPhase) : 0;

            for (let i = 0; i < wCfg.numSeg; i++) {
                const a0 = rot + i * wCfg.segDeg * DEG2RAD;
                const a1 = rot + (i + 1) * wCfg.segDeg * DEG2RAD;
                const mid = (a0 + a1) / 2;
                const col = COLORS[i % COLORS.length];

                ctx.beginPath();
                ctx.moveTo(cx, cy);
                ctx.arc(cx, cy, segR, a0, a1);
                ctx.closePath();
                ctx.fillStyle = col;
                ctx.fill();

                const gx = cx + Math.cos(mid) * segR;
                const gy = cy + Math.sin(mid) * segR;
                const sg = ctx.createLinearGradient(cx, cy, gx, gy);
                sg.addColorStop(0.00, 'rgba(255,255,255,0.28)');
                sg.addColorStop(0.40, 'rgba(255,255,255,0.06)');
                sg.addColorStop(1.00, 'rgba(0,0,0,0.18)');
                ctx.beginPath();
                ctx.moveTo(cx, cy);
                ctx.arc(cx, cy, segR, a0, a1);
                ctx.closePath();
                ctx.fillStyle = sg;
                ctx.fill();

                ctx.strokeStyle = 'rgba(0,0,0,0.14)';
                ctx.lineWidth = 0.7;
                ctx.stroke();

                if (i === winSeg && glowAlpha > 0) {
                    ctx.save();
                    ctx.beginPath();
                    ctx.moveTo(cx, cy);
                    ctx.arc(cx, cy, segR, a0, a1);
                    ctx.closePath();
                    ctx.fillStyle = `rgba(255,255,180,${glowAlpha})`;
                    ctx.fill();
                    ctx.shadowColor = '#FFD700';
                    ctx.shadowBlur = 18;
                    ctx.beginPath();
                    ctx.moveTo(cx, cy);
                    ctx.arc(cx, cy, segR, a0, a1);
                    ctx.closePath();
                    ctx.strokeStyle = 'rgba(255,215,0,0.7)';
                    ctx.lineWidth = 3;
                    ctx.stroke();
                    ctx.restore();
                }

                // Label
                ctx.save();
                ctx.translate(cx, cy);
                ctx.rotate(mid);
                const label = wCfg.segments[i];
                const fontSize = Math.max(11, W * 0.034);
                const textR = segR * 0.93;
                ctx.font = `900 ${fontSize}px "Arial Black","Arial",sans-serif`;
                ctx.textAlign = 'right';
                ctx.textBaseline = 'middle';
                ctx.strokeStyle = 'rgba(0,0,0,0.95)';
                ctx.lineWidth = Math.max(2.5, fontSize * 0.22);
                ctx.lineJoin = 'round';
                ctx.strokeText(label, textR, 0);
                ctx.fillStyle = PRIZE_ACCENT[label] || 'white';
                ctx.fillText(label, textR, 0);
                ctx.restore();
            }

            // Spoke lines
            for (let i = 0; i < wCfg.numSeg; i++) {
                const angle = rot + i * wCfg.segDeg * DEG2RAD;
                ctx.beginPath();
                ctx.moveTo(cx + Math.cos(angle) * (hubR + 3), cy + Math.sin(angle) * (hubR + 3));
                ctx.lineTo(cx + Math.cos(angle) * segR, cy + Math.sin(angle) * segR);
                ctx.strokeStyle = 'rgba(0,0,0,0.18)';
                ctx.lineWidth = 1;
                ctx.stroke();
            }

            // Gold pegs
            const pegR = W * 0.007;
            for (let i = 0; i < wCfg.numSeg; i++) {
                const angle = rot + i * wCfg.segDeg * DEG2RAD;
                const px = cx + Math.cos(angle) * (segR - pegR * 0.4);
                const py = cy + Math.sin(angle) * (segR - pegR * 0.4);
                ctx.save();
                ctx.shadowColor = 'rgba(0,0,0,0.5)';
                ctx.shadowBlur = 4;
                ctx.shadowOffsetX = 1;
                ctx.shadowOffsetY = 1;
                const pegGrad = ctx.createRadialGradient(px - pegR * 0.35, py - pegR * 0.35, pegR * 0.05, px, py, pegR);
                pegGrad.addColorStop(0.00, '#FFFDE0');
                pegGrad.addColorStop(0.30, '#FFD700');
                pegGrad.addColorStop(0.70, '#DAA520');
                pegGrad.addColorStop(1.00, '#8B6400');
                ctx.beginPath();
                ctx.arc(px, py, pegR, 0, Math.PI * 2);
                ctx.fillStyle = pegGrad;
                ctx.fill();
                ctx.strokeStyle = 'rgba(100,60,0,0.7)';
                ctx.lineWidth = 0.8;
                ctx.stroke();
                ctx.restore();
            }

            // Hub
            ctx.beginPath();
            ctx.arc(cx, cy, hubR + 5, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(0,0,0,0.3)';
            ctx.fill();

            const hubGrad = ctx.createRadialGradient(
                cx - hubR * 0.32, cy - hubR * 0.32, hubR * 0.04,
                cx + hubR * 0.12, cy + hubR * 0.12, hubR
            );
            hubGrad.addColorStop(0.00, '#FFFFF0');
            hubGrad.addColorStop(0.22, '#FFD700');
            hubGrad.addColorStop(0.58, '#DAA520');
            hubGrad.addColorStop(0.84, '#B8860B');
            hubGrad.addColorStop(1.00, '#704000');
            ctx.save();
            ctx.shadowColor = 'rgba(0,0,0,0.4)';
            ctx.shadowBlur = 8;
            ctx.shadowOffsetX = 2;
            ctx.shadowOffsetY = 3;
            ctx.beginPath();
            ctx.arc(cx, cy, hubR, 0, Math.PI * 2);
            ctx.fillStyle = hubGrad;
            ctx.fill();
            ctx.restore();
            ctx.beginPath();
            ctx.arc(cx, cy, hubR, 0, Math.PI * 2);
            ctx.strokeStyle = 'rgba(80,40,0,0.7)';
            ctx.lineWidth = 2;
            ctx.stroke();
            ctx.beginPath();
            ctx.ellipse(cx - hubR * 0.28, cy - hubR * 0.32, hubR * 0.22, hubR * 0.14, -Math.PI / 4, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(255,255,255,0.5)';
            ctx.fill();
            ctx.beginPath();
            ctx.arc(cx, cy, hubR * 0.55, 0, Math.PI * 2);
            ctx.strokeStyle = 'rgba(180,120,0,0.5)';
            ctx.lineWidth = 1.5;
            ctx.stroke();

            // Stopper / pointer
            const pointerPegR = W * 0.007;
            const baseX = cx + outerR + W * 0.008;
            const halfH = W * 0.030;
            const tipNomX = cx + (segR - pointerPegR * 2.5);
            const armLen = baseX - tipNomX;
            const rotAngle = s.pDeflect / armLen;
            ctx.save();
            ctx.shadowColor = 'rgba(0,0,0,0.5)';
            ctx.shadowBlur = 12;
            ctx.shadowOffsetX = 4;
            ctx.shadowOffsetY = 3;
            ctx.translate(baseX, cy);
            ctx.rotate(rotAngle);
            ctx.translate(-baseX, -cy);
            ctx.beginPath();
            ctx.moveTo(tipNomX, cy);
            ctx.lineTo(baseX, cy - halfH);
            ctx.arc(baseX, cy, halfH, -Math.PI / 2, Math.PI / 2);
            ctx.closePath();
            const pGrad = ctx.createLinearGradient(tipNomX, cy - halfH, baseX + halfH, cy + halfH);
            pGrad.addColorStop(0.00, '#FFFFFF');
            pGrad.addColorStop(0.25, '#F4F4F4');
            pGrad.addColorStop(0.65, '#DEDEDE');
            pGrad.addColorStop(1.00, '#C0C0C0');
            ctx.fillStyle = pGrad;
            ctx.fill();
            ctx.strokeStyle = '#888888';
            ctx.lineWidth = 2;
            ctx.stroke();
            // Top edge highlight
            ctx.beginPath();
            ctx.moveTo(tipNomX + 5, cy - 1);
            ctx.lineTo(baseX - 3, cy - halfH + 4);
            ctx.strokeStyle = 'rgba(255,255,255,0.8)';
            ctx.lineWidth = 2;
            ctx.stroke();
            // Gold pin/rivet in the round base
            const pinR = halfH * 0.38;
            ctx.beginPath();
            ctx.arc(baseX, cy, pinR, 0, Math.PI * 2);
            const pinGrad = ctx.createRadialGradient(baseX - pinR * 0.3, cy - pinR * 0.3, 0, baseX, cy, pinR);
            pinGrad.addColorStop(0, '#FFE066');
            pinGrad.addColorStop(0.5, '#DAA520');
            pinGrad.addColorStop(1, '#B8860B');
            ctx.fillStyle = pinGrad;
            ctx.fill();
            ctx.strokeStyle = '#8B6914';
            ctx.lineWidth = 1.2;
            ctx.stroke();
            ctx.restore();
        }

        function loop() {
            const s = S.current;
            const now = performance.now();

            if (s.isSpinning) {
                const elapsedMs = now - s.spinStartTime;
                const progress = Math.min(elapsedMs / s.spinDuration, 1);
                const remaining = Math.max(0, 1 - progress);

                // Uses an exactly corresponding time-parametric ease-out function
                // so the final position is perfectly deterministic and independent of frame rate.
                const easeOut = 1 - Math.pow(remaining, 3.15);
                const nextRotation = s.startRotation + s.totalRotation * easeOut;

                s.velocity = nextRotation - s.rotation;
                s.rotation = nextRotation;

                if (progress >= 1) {
                    finalizeSpin();
                }

                const { segDeg: rSegDeg, numSeg: rNumSeg } = wheelConfigRef.current;
                const cur = segAtPointer(s.rotation, rSegDeg, rNumSeg);
                if (cur !== s.lastSeg) {
                    let count = 0, tmp = s.lastSeg;
                    while (tmp !== cur && count < rNumSeg) { tmp = (tmp - 1 + rNumSeg) % rNumSeg; count++; }

                    // Realistic peg-hitting: light taps when fast, heavy catches when slow
                    const speed = Math.abs(s.velocity);
                    let bumpMag;
                    if (speed > 3) {
                        // Fast spin: light flicks
                        bumpMag = 6 + speed * 0.15;
                    } else if (speed > 0.5) {
                        // Medium speed: noticeable bounces  
                        bumpMag = 12 + (3 - speed) * 5;
                    } else {
                        // Very slow / final pegs: dramatic heavy catch
                        bumpMag = 25 + (0.5 - speed) * 20;
                    }
                    s.pDeflect = Math.min(bumpMag, 45) * Math.min(count, 2);
                    s.pVel = 0;
                    playTick(s.velocity);
                    s.lastSeg = cur;
                }
            }

            // Pointer spring â€” realistic damped oscillation
            const pSegDeg = wheelConfigRef.current.segDeg;
            const rawAngle = ((-s.rotation % pSegDeg) + pSegDeg) % pSegDeg;
            const fracInSeg = rawAngle / pSegDeg;
            const nearestPeg = Math.min(fracInSeg, 1 - fracInSeg) * 2;
            const pegPress = (1 - nearestPeg) * 16;

            // When wheel is stopped or nearly stopped, pointer settles firmly against the peg
            const isSettling = !s.isSpinning || Math.abs(s.velocity) < 0.3;
            const damping = isSettling ? 0.70 : 0.62;
            const stiffness = isSettling ? 0.18 : 0.24;
            const REST_BIAS = pegPress; // Horizontal at rest

            s.pVel = s.pVel * damping - stiffness * (s.pDeflect - REST_BIAS);
            s.pDeflect += s.pVel;

            // PHYSICAL FEEDBACK: The wheel "feels" the pointer.
            // We apply a tiny visual offset to the wheel's rotation based on pointer deflection
            // This makes the wheel look like it's being held back by the peg.
            const physOffset = s.pDeflect * 0.0015;
            const currentDrawRotation = s.rotation - physOffset;

            if (Math.abs(s.pDeflect - REST_BIAS) < 0.05 && Math.abs(s.pVel) < 0.05) {
                s.pDeflect = REST_BIAS;
                s.pVel = 0;
            }

            if (s.winSeg !== null && !s.isSpinning) s.glowPhase += 0.07;

            draw(currentDrawRotation);
            rafId = requestAnimationFrame(loop);
        }

        setupCanvas();
        window.addEventListener('resize', setupCanvas);
        rafId = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('resize', setupCanvas);
        };
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (spinning) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [spinning]);

    // â”€â”€ Bet placement â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    function handleBetClick(label) {
        if (interactionsLocked) return;
        if (!isFreeSpinMode && balance < selectedCoin) return;

        // Selection Limit Logic
        const currentSelectedCount = Object.values(bets).filter(box =>
            Object.values(box).some(count => count > 0)
        ).length;

        const isNewSelection = !bets[label] || !Object.values(bets[label]).some(count => count > 0);
        const maxSelectable = spinAndWinLimit?.maxSelectableMultipliers || 4;

        if (isNewSelection && currentSelectedCount >= maxSelectable) {
            setAlertMsg(`Maximum ${maxSelectable} multipliers can be selected`);
            setTimeout(() => setAlertMsg(null), 2500);
            return;
        }

        setBets(prev => {
            const box = prev[label] || {};
            const prevCount = box[selectedCoin] || 0;

            return {
                ...prev,
                [label]: {
                    ...box,
                    [selectedCoin]: prevCount + 1
                }
            };
        });

        if (!isFreeSpinMode) {
            setBalance(prev => prev - selectedCoin);
        }
    }

    // â”€â”€ Cancel bets â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    function handleCancel() {
        if (interactionsLocked) return;
        if (!isFreeSpinMode) {
            setBalance(prev => prev + totalStake);
        }
        setBets(makeEmptyBets());
    }

    function applyPreparedBet(nextBet, coinValue) {
        if (interactionsLocked || !nextBet) return;

        const refundedBalance = balance + (isFreeSpinMode ? 0 : totalStake);
        const preparedStake = Object.values(nextBet).reduce((sum, box) => {
            return sum + Object.entries(box).reduce((s, [coin, count]) => {
                return s + Number(coin) * count;
            }, 0);
        }, 0);

        if (preparedStake <= 0) return;
        if (!isFreeSpinMode && refundedBalance < preparedStake) return;

        setSelectedCoin(coinValue);
        setBets({ ...nextBet });
        if (!isFreeSpinMode) {
            setBalance(refundedBalance - preparedStake);
        }
        setIsDrawerOpen(false);
    }

    // â”€â”€ Rebet â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    function handleRebet() {
        if (interactionsLocked || !lastBet) return;
        applyPreparedBet(lastBet.bets, lastBet.coin);
    }

    function handleDoubleBet() {
        if (interactionsLocked || !lastBet) return;
        // const doubledBets = Object.entries(lastBet.bets).reduce((acc, [label, count]) => {
        //   acc[label] = count * 2;
        //   return acc;
        // }, {});
        const doubledBets = Object.entries(lastBet.bets).reduce((acc, [label, box]) => {
            acc[label] = Object.fromEntries(
                Object.entries(box).map(([coin, count]) => [coin, count * 2])
            );
            return acc;
        }, {});
        applyPreparedBet(doubledBets, lastBet.coin);
    }

    // â”€â”€ Spin â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    function handleSpin(isFree = false, isReward = false, customBets = null) {
        const finalIsFree = isFree || isFreeSpinMode;
        const finalIsReward = isReward || isFreeSpinMode;

        activeSpinBetsRef.current = customBets; // Store custom bets for this specific spin cycle

        currentSpinIsRewardRef.current = finalIsReward;
        if (S.current.isSpinning) return;
        if (!finalIsFree && !hasBet) return;

        if (!isFree) {
            const minBet = gameSettings?.minPrice || 1;
            const maxBet = gameSettings?.maxPrice || 1000000;
            if (totalStake < minBet) {
                setAlertMsg(`Minimum bet amount is ${minBet}`);
                setTimeout(() => setAlertMsg(null), 2500);
                return;
            }
            if (totalStake > maxBet) {
                setAlertMsg(`Maximum bet amount is ${maxBet}`);
                setTimeout(() => setAlertMsg(null), 2500);
                return;
            }
        }

        if (!finalIsFree) {
            isFreeSpinPayoutDoneRef.current = false;
        }

        currentSpinSourceRef.current = finalIsFree ? 'free' : 'manual';
        if (isFreeSpinMode) {
            setFreeSpinsLeft(prev => {
                const remaining = Math.max(0, prev - 1);
                if (remaining === 0) setIsFreeSpinMode(false);
                return remaining;
            });
        }
        if (!audioCtxRef.current) {
            audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        if (audioCtxRef.current.state === 'suspended') audioCtxRef.current.resume();

        const s = S.current;
        s.rotation = 0;

        let totalRot = 360 * (5 + Math.random() * 3); // 5 to 8 full rotations
        const duration = SPEEDS[selectedSpeed]?.duration ?? SPEEDS.normal.duration;

        const limitData = spinAndWinLimitRef.current;
        // Always check if we need to avoid segments (e.g. isWin:false or free spin limit reached)
        {
            let canAvoid = false;
            for (let i = 0; i < wheelConfigRef.current.numSeg; i++) {
                if (!isWinningSegment(i)) {
                    canAvoid = true;
                    break;
                }
            }

            if (canAvoid) {
                let finalAngle = s.rotation + totalRot;
                const { segDeg: hSegDeg, numSeg: hNumSeg } = wheelConfigRef.current;
                while (isWinningSegment(segAtPointer(finalAngle, hSegDeg, hNumSeg))) {
                    totalRot += hSegDeg;
                    finalAngle = s.rotation + totalRot;
                }
            }
        }

        // CENTER THE LANDING: adjust totalRot so the pointer is in the exact middle of the segment
        const { segDeg: cSegDeg, numSeg: cNumSeg } = wheelConfigRef.current;
        const currentFinalAngle = s.rotation + totalRot;
        const currentSegmentIndex = segAtPointer(currentFinalAngle, cSegDeg, cNumSeg);
        const currentRawAngle = ((-currentFinalAngle % 360) + 360) % 360;
        const targetRawAngle = (currentSegmentIndex + 0.5) * cSegDeg;

        let rawOffset = targetRawAngle - currentRawAngle;
        // Normalize rawOffset to [-180, 180] for shortest path
        if (rawOffset > 180) rawOffset -= 360;
        if (rawOffset < -180) rawOffset += 360;

        // Since raw = -rotation, totalRot increment = -rawOffset
        totalRot -= rawOffset;
        if (totalRot < 360 * 5) totalRot += 360; // Keep minimum spins

        s.startRotation = s.rotation;
        s.totalRotation = totalRot;
        s.spinDuration = duration;
        s.isSpinning = true;
        s.isSliding = false;
        s.spinStartTime = performance.now();
        s.lastSeg = segAtPointer(s.rotation, wheelConfigRef.current.segDeg, wheelConfigRef.current.numSeg);
        s.winSeg = null;
        if (!isFree) {
            setLastBet({ bets: { ...bets }, coin: selectedCoin });
        }
        setResult(null);
        setSpinning(true);
    }

    triggerSpinRef.current = handleSpin;

    const formatBetsForAPI = (bets, currentResult = '') => {
        const resLabel = /x/i.test(currentResult) ? currentResult.replace(/x/gi, '') : currentResult;

        return Object.entries(bets).flatMap(([multiplier, box]) =>
            Object.entries(box).map(([coin, count]) => {
                const multLabel = multiplier.replace(/x/gi, '');
                const status = multLabel === resLabel ? 'win' : 'lose';

                return {
                    multiplier: multLabel,
                    amount: String(Number(coin) * count),
                    status,
                };
            })
        );
    };

    useEffect(() => {
        getWallet();
        fetchSpinSettings();
        fetchJackpotSettings();
        fetchPoolSettings();
    }, []);

    const fetchSpinSettings = async () => {
        try {
            const { data } = await Axios.post('/spinWheel/get-trade-settings', { type: 'spinAndWin' });
            if (data?.success && data?.result?.isActive) {
                const result = data.result;
                setGameSettings({
                    ...result,
                    minPrice: Number(result.min_amount || 1),
                    maxPrice: Number(result.max_amount || 1000000),
                });
                setSpinAndWinLimit(prev => ({
                    dailyWinLimit: Number(result.daily_win_limit || 3),
                    dailyFreeSpinLimit: Number(result.spinAndWin?.daily_free_spin_limit || 2),
                    currentWinCount: prev?.currentWinCount ?? 0,
                    currentFreeSpinCount: prev?.currentFreeSpinCount ?? 0,
                    maxSelectableMultipliers: Number(result.spinAndWin?.max_selection || 4),

                }));
                const spinSettings = result.spinAndWin?.spin_settings || [];
                const cfg = buildWheelConfig(spinSettings);
                if (cfg) {
                    setWheelConfig(cfg);
                    setBets(cfg.emptyBets);
                    freeSpinBetRef.current = { ...cfg.emptyBets };
                    betsRef.current = { ...cfg.emptyBets };
                }
            }
        } catch (error) {
            console.error('Error fetching spin settings:', error);
        }
    };

    const fetchJackpotSettings = async () => {
        try {
            const { data } = await Axios.post('/spinWheel/get-jackpot-settings');
            if (data?.success) {
                const activeSettings = Array.isArray(data.result)
                    ? data.result.find((i) => i?.isActive)
                    : data.result;
                if (activeSettings?.isActive) {
                    const segs = buildJackpotSegments(activeSettings.spin_settings || []);
                    if (segs) setJackpotSegments(segs);
                }
            }
        } catch (error) {
            console.error('Error fetching jackpot settings:', error);
        }
    };

    const fetchPoolSettings = async () => {
        try {
            const { data } = await Axios.post('/spinWheel/get-pool-settings');
            if (data?.success) {
                setJackpotPool(Number(data.result?.price || 0));
            }
        } catch (error) {
            console.error('Error fetching pool settings:', error);
        }
    };

    const updateTutorialStatus = async (status) => {
        try {
            await Axios.post('/users/update-spin-tutorial-status', { status });
            setWallet(prev => ({ ...prev, spinAndWinTutorialStatus: status }));
        } catch (error) {
            console.error('Error updating tutorial status:', error);
        }
    };

    const openJackpotWheel = () => {
        if (!canOpenJackpotWheel) return;
        setIsResultPending(true);
        setShowJackpotWheel(true);
    };

    const getWallet = async () => {
        try {
            const { data } = await Axios.post('/users/getWallet', {});
            if (data?.success) {
                setWallet(data.result);
                if (setGlobalWallet) setGlobalWallet(data.result);
                if (!isFreeSpinPayoutDoneRef.current) {
                    const role = String(window.localStorage.getItem('use') || '').trim();
                    const walletBalance = role === 'demo'
                        ? Number(data.result?.demo_balance || 0)
                        : Number(data.result?.balance || 0);
                    setBalance(walletBalance);
                }
                // Sync daily free spin count from wallet
                const limit = data.result?.spin_and_win_limit;
                if (limit) {
                    setSpinAndWinLimit(prev => ({
                        ...(prev || {}),
                        dailyWinLimit: limit.daily_win_limit ?? prev?.dailyWinLimit ?? 0,
                        dailyFreeSpinLimit: limit.daily_free_spin_limit ?? prev?.dailyFreeSpinLimit ?? 0,
                        currentWinCount: limit?.current_win_count ?? prev?.currentWinCount ?? 0,
                        currentFreeSpinCount: limit?.current_free_spin_count ?? prev?.currentFreeSpinCount ?? 0,
                        freeSpinRewardCount: limit?.current_reward_spin_count ?? prev?.freeSpinRewardCount ?? 0,
                        jackpotClaimedToday: limit?.jackpotClaimedToday ?? prev?.jackpotClaimedToday ?? false,
                    }));
                    if (typeof limit.current_reward_spin_count === 'number') {
                        setFreeSpinsLeft(limit.current_reward_spin_count);
                    }
                }
            } else {
                setWallet({ balance: 0 });
                if (!isFreeSpinPayoutDoneRef.current) setBalance(0);
            }
        } catch (error) {
            setWallet({ balance: 0 });
            if (!isFreeSpinPayoutDoneRef.current) setBalance(0);
            console.error('Error fetching wallet:', error);
        }
    };

    const createSpinBet = async (payload, options = {}) => {
        const { refreshWallet = true } = options;

        try {
            const { data } = await Axios.post('/spinWheel/create-spin-bet', payload);


            if (!data?.success) {
                console.error("API Error:", data?.message);
            }

            // Immediately apply updated tracking counts so isWinningSegment is always current
            if (data?.result?.spinAndWinLimit) {
                setSpinAndWinLimit(prev => ({
                    ...(prev || {}),
                    ...data.result.spinAndWinLimit,
                }));
            }

            if (refreshWallet) {
                await getWallet();
            }

            return data;
        } catch (err) {
            console.error("API Failed:", err);
        }
    };

    // â”€â”€ Resolve result â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    useEffect(() => {
        if (!result) return;
        let isCancelled = false;

        const resolveSpin = async () => {
            const completedSpinSource = completedSpinSourceRef.current ?? 'manual';
            const isFreeSpin = completedSpinSource === 'free';
            const betSnapshot = activeSpinBetsRef.current || betsRef.current;
            const fallbackWinAmount = calculateBetWinAmount(result, betSnapshot);

            setIsResultPending(true);
            clearLossModalTimeout();

            const currentRes = /x/i.test(result) ? result.replace(/x/gi, '') : result;
            const calcBetAmount = Object.values(betSnapshot).reduce((sum, box) => {
                return sum + Object.entries(box).reduce((s, [coin, count]) => {
                    return s + Number(coin) * count;
                }, 0);
            }, 0);

            const payload = {
                bet_amount: String(calcBetAmount),
                bets: formatBetsForAPI(betSnapshot, result),
                result: currentRes,
                isFree: String(isFreeSpin),
                isReward: String(currentSpinIsRewardRef.current),
                isFreeCount: String(result.includes('spin'))
            };

            const data = await createSpinBet(payload);
            const apiData = data?.success ? data : null;

            if (apiData) {
                if (typeof apiData.updatedBalance === 'number') {
                    setBalance(apiData.updatedBalance);
                }

                if (typeof apiData.freeSpinCount === 'number') {
                    setSpinAndWinLimit(prev => ({
                        ...(prev || {}),
                        currentFreeSpinCount: apiData.freeSpinCount,
                        dailyFreeSpinLimit: apiData.dailyFreeSpinLimit,
                        jackpotClaimedToday: apiData.jackpotClaimedToday
                    }));
                }

                if (typeof apiData.freeSpinsLeft === 'number') {
                    setFreeSpinsLeft(apiData.freeSpinsLeft);
                }
            }

            if (isCancelled) return;

            const resolvedWinAmount = extractSpinWinAmount(apiData, fallbackWinAmount);

            if (result.includes('spin')) {
                const spins = parseInt(result, 10);

                // If it is a manual spin or free spin that lands on free spins, keep/refresh bets
                const shouldKeepBets = completedSpinSource === 'autoplay' || completedSpinSource === 'free';
                if (!shouldKeepBets) {
                    setBets(makeEmptyBets());
                }

                setWinModal({
                    win: true,
                    label: result,
                    amount: null,
                    title: `You won ${spins} free ${spins === 1 ? 'spin' : 'spins'}!`,
                    subtitle: 'added to your free spins',
                    continueText: 'Continue',
                    pendingFreeSpins: null,
                    autoClose: false,
                    mode: 'result',
                });
                return;
            }

            const lostAmount = isFreeSpin ? 0 : totalStakeRef.current;
            const shouldKeepBets = completedSpinSource === 'autoplay' || completedSpinSource === 'free';

            if (!shouldKeepBets) {
                setBets(makeEmptyBets());
            }

            if (apiData?.jackpotWon) {
                setShowJackpotWheel(true);
                setIsResultPending(true);
                return;
            }

            if (resolvedWinAmount > 0) {
                setWinModal({
                    win: true,
                    label: result,
                    amount: resolvedWinAmount,
                    title: 'You Won!',
                    subtitle: 'added to your balance',
                    continueText: 'Continue',
                    pendingFreeSpins: null,
                    autoClose: false,
                    mode: 'result',
                });
            } else if (lostAmount > 0) {
                setWinModal({
                    win: false,
                    label: result,
                    amount: lostAmount,
                    title: 'Better Luck!',
                    subtitle: 'lost this round',
                    continueText: null,
                    pendingFreeSpins: null,
                    autoClose: true,
                    mode: 'result',
                });
                lossModalTimeoutRef.current = setTimeout(() => {
                    setWinModal(null);
                    setIsResultPending(false);
                    lossModalTimeoutRef.current = null;
                }, 3000);
            } else {
                setWinModal({
                    win: false,
                    label: result,
                    amount: null,
                    title: 'Better Luck!',
                    subtitle: 'try again next round',
                    continueText: null,
                    pendingFreeSpins: null,
                    autoClose: true,
                    mode: 'result',
                });
                lossModalTimeoutRef.current = setTimeout(() => {
                    setWinModal(null);
                    setIsResultPending(false);
                    lossModalTimeoutRef.current = null;
                }, 3000);
            }
        };

        resolveSpin();

        return () => {
            isCancelled = true;
        };
    }, [result]);

    // â”€â”€ Free spin button handler (manual trigger) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    function handleFreeSpinClick() {
        if (freeSpinsLeft <= 0) {
            setAlertMsg('No free spins available. Land on it to win more!');
            setTimeout(() => setAlertMsg(null), 3000);
            return;
        }

        if (S.current.isSpinning || isResultPending) return;

        // Toggle free spin mode
        if (!isFreeSpinMode) {
            // Entering free spin mode: refund and clear any existing real bets
            handleCancel();
            setIsFreeSpinMode(true);
            setAlertMsg('Free Spin mode active. Place your bets!');
        } else {
            // Exiting free spin mode: clear any free bets placed
            setBets(makeEmptyBets());
            setIsFreeSpinMode(false);
            setAlertMsg('Free Spin mode deactivated.');
        }
        setTimeout(() => setAlertMsg(null), 2500);
    }

    useEffect(() => {
        if (!completedSpinId || spinning || isResultPending || completedSpinSourceRef.current !== 'autoplay') {
            return;
        }

        // Guard: prevent double-decrement if this effect fires multiple times for the same spin
        if (completedSpinId <= lastProcessedAutoplaySpinIdRef.current) {
            return;
        }
        lastProcessedAutoplaySpinIdRef.current = completedSpinId;

        const nextRemaining = autoplayRemainingRef.current - 1;
        autoplayRemainingRef.current = nextRemaining;
        setAutoplayCount(nextRemaining);

        if (nextRemaining <= 0 || !isAutoplayActiveRef.current) {
            clearAutoplayTimeout();
            setIsAutoplayActive(false);
            return;
        }

        clearAutoplayTimeout();
        autoplayTimeoutRef.current = setTimeout(() => {
            autoplayTimeoutRef.current = null;

            if (!isAutoplayActiveRef.current || S.current.isSpinning || isResultPending) {
                return;
            }

            const nextStake = totalStakeRef.current;

            if (nextStake <= 0 || !hasBetRef.current) {
                setAutoplayCount(0);
                setIsAutoplayActive(false);
                return;
            }

            if (balanceRef.current < nextStake) {
                setAlertMsg('Insufficient balance to continue Autoplay');
                setTimeout(() => setAlertMsg(null), 2500);
                setAutoplayCount(0);
                setIsAutoplayActive(false);
                return;
            }

            setBalance(previous => previous - nextStake);
            currentSpinSourceRef.current = 'autoplay';
            triggerSpinRef.current?.(false);
        }, 200);

        return () => clearAutoplayTimeout();
    }, [completedSpinId, spinning, isResultPending]);

    useEffect(() => {
        if (!isAutoplayModalOpen) return undefined;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setIsAutoplayModalOpen(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isAutoplayModalOpen]);

    useEffect(() => () => {
        clearAutoplayTimeout();
        clearLossModalTimeout();
    }, []);

    // â”€â”€ Canvas click â†’ hub spin â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    function handleCanvasClick(e) {
        if (interactionsLocked) return;
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        const W = canvas.width / dpr;
        const cx = W / 2;
        const cy = W / 2;
        const hubR = W * 0.074;
        const mx = (e.clientX - rect.left) * (W / rect.width);
        const my = (e.clientY - rect.top) * (W / rect.height);
        if (Math.hypot(mx - cx, my - cy) <= hubR) handleSpin();
    }

    function handleOpenAutoplayModal() {
        if (isAutoplayActive) return;
        setIsDrawerOpen(false);
        setIsAutoplayModalOpen(true);
    }

    function stopAutoplaySequence() {
        clearAutoplayTimeout();
        autoplayRemainingRef.current = 0;
        setAutoplayCount(0);
        setIsAutoplayActive(false);
    }

    function handleAutoplayOptionSelect(option) {
        if (S.current.isSpinning) return;
        if (!hasBetRef.current) {
            setAlertMsg('Place a bet before starting Autoplay');
            setTimeout(() => setAlertMsg(null), 2500);
            setIsAutoplayModalOpen(false);
            return;
        }

        setIsAutoplayModalOpen(false);
        clearAutoplayTimeout();
        autoplayRemainingRef.current = option;
        lastProcessedAutoplaySpinIdRef.current = completedSpinId;
        setAutoplayCount(option);
        setIsAutoplayActive(true);
        currentSpinSourceRef.current = 'autoplay';
        handleSpin(false);
    }

    function handleStopWheel() {
        stopAutoplaySequence();
        if (S.current.isSpinning) {
            finalizeSpin();
        }
    }

    function handleResultModalClose() {
        clearLossModalTimeout();
        setWinModal(null);
        setIsResultPending(false);
    }

    function handleResultContinue() {
        const pendingFreeSpins = winModal?.pendingFreeSpins ?? null;
        const modalMode = winModal?.mode ?? null;
        const pendingFreeSpinTotal = freeSpinTotalWinRef.current;
        clearLossModalTimeout();
        setWinModal(null);
        setIsResultPending(false);

        if (pendingFreeSpins) {
            const nextLockedBets = cloneBetState(lastBet?.bets || betsRef.current);
            freeSpinBetRef.current = nextLockedBets;
            setLockedBets(nextLockedBets);
            updateFreeSpinTotalWin(0);
        }

        if (modalMode === 'free-spin-summary') {
            freeSpinBetRef.current = makeEmptyBets();
            setLockedBets(null);
            setBets(makeEmptyBets());
            updateFreeSpinTotalWin(0);
        }
    }

    // â”€â”€ Render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const selectedCoinData = COINS.find(c => c.value === selectedCoin) || COINS[6];

    return (
        <div className="spin-page-wrap">
            {/* Mobile Jackpot Spin UI (< 768px) */}
            <div className="block md:hidden w-full h-[calc(100vh-65px)]">
                <MobileJackpotSpin
                    wallet={wallet}
                    jackpotPool={jackpotPool}
                    jackpotTickets={jackpotTickets}
                    segments={jackpotSegments}
                    onSpinResult={(res) => {
                        console.log('Mobile Jackpot result received:', res);
                        getWallet();
                    }}
                />
            </div>

            {/* Desktop Spin Wheel UI (>= 768px) */}
            <div className="hidden md:block w-full">
                <div className="spin-game-box">
                    <div className="sw-root">
                    <div className="sw-bg-overlay" />

                    {/* Header */}
                    <div className="sw-header">
                        <h1 className="sw-title">
                            {['S', 'P', 'I', 'N', ' ', 'W', 'H', 'E', 'E', 'L'].map((ch, i) => (
                                <span key={i} className="sw-title-text">
                                    {ch === ' ' ? '\u00A0' : ch}
                                </span>
                            ))}
                        </h1>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                            <div className="sw-balance-area">
                                <span className="sw-balance-label">Balance</span>
                                <div className="sw-balance-value">{Number(balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                            </div>
                            <div className="sw-balance-area">
                                <span className="sw-balance-label">Jackpot Pool</span>
                                <div className="sw-balance-value">{Number(jackpotPool || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                            </div>
                            {/* <div className="sw-balance-area">
                                <span className="sw-balance-label">Jackpot Tickets</span>
                                <div className="sw-balance-value">{jackpotTickets}</div>
                            </div>
                            {canOpenJackpotWheel && (
                                <button
                                    type="button"
                                    className="sw-jackpot-launch-btn"
                                    onClick={openJackpotWheel}
                                >
                                    Open Jackpot Spin
                                </button>
                            )} */}
                        </div>
                    </div>

                    {/* Game area */}
                    <div className="sw-game-area">
                        {/* Left panel */}
                        <div className="sw-side-panel">
                            {isAutoplayActive ? (
                                <button
                                    type="button"
                                    className="sw-btn sw-btn--stop-autoplay"
                                    onClick={stopAutoplaySequence}
                                >
                                    Stop Autoplay
                                </button>
                            ) : (
                                <button id="tour-info-btn" className="sw-btn sw-btn--info" onClick={() => setShowInfoDrawer(true)}>Info</button>
                            )}
                            <button
                                id="tour-drawer-btn"
                                type="button"
                                className="sw-btn sw-btn--toggle-drawer"
                                onClick={() => {
                                    if (isAutoplayActive) return;
                                    setIsDrawerOpen(previous => !previous);
                                }}
                                aria-expanded={isDrawerOpen}
                                aria-controls="sw-bottom-drawer"
                                aria-label={isDrawerOpen ? 'Close action drawer' : 'Open action drawer'}
                            >
                                {/* <span className={`sw-toggle-arrow${isDrawerOpen ? ' is-open' : ''}`} aria-hidden="true" /> */}

                                {!isDrawerOpen ? (
                                    <span className="sw-toggle-currency">â‚¦</span>
                                ) : (
                                    <span
                                        className={`sw-toggle-arrow${isDrawerOpen ? ' is-open' : ''}`}
                                        aria-hidden="true"
                                    />
                                )}
                            </button>
                        </div>

                        {/* Wheel */}
                        <div className="sw-wheel-wrap">
                            <canvas
                                ref={canvasRef}
                                onClick={handleCanvasClick}
                                style={{ display: 'block', cursor: interactionsLocked ? 'default' : 'pointer' }}
                            />

                            {isFreeSpinMode && (
                                <div className="sw-free-mode-banner">
                                    FREE SPINS ACTIVE
                                </div>
                            )}

                            {showCoinPicker && (
                                <div className="sw-coin-picker">
                                    {COINS.map(c => (
                                        <div
                                            key={c.value}
                                            className="sw-coin-wrap"
                                            onClick={() => {
                                                if (interactionsLocked) return;
                                                setSelectedCoin(c.value);
                                                setShowCoinPicker(false);
                                            }}
                                        >
                                            <CasinoChip value={c.value} color={c.color} stripe={c.stripe} size={58} selected={selectedCoin === c.value} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Right panel */}
                        <div className="sw-side-panel">
                            {isAutoplayActive ? (
                                <button
                                    type="button"
                                    className="sw-btn sw-btn--stop-wheel"
                                    style={{
                                        opacity: spinning ? 1 : 0.8,
                                        background: 'radial-gradient(circle at 30% 30%, #ff8c8c 0%, #e03232 40%, #9f1010 75%, #5e0000 100%)',
                                    }}
                                    onClick={handleStopWheel}
                                >
                                    Stop Wheel
                                </button>
                            ) : hasBet || !hasPreviousBet ? (
                                <button
                                    id="tour-spin-btn"
                                    className="sw-btn sw-btn--spin"
                                    style={{
                                        opacity: (!hasBet || interactionsLocked) ? 0.5 : 1,
                                        background: (!hasBet || interactionsLocked)
                                            ? "radial-gradient(circle at 35% 32%, #c0c8d0, #7a8898 54%, #525c68)"
                                            : "radial-gradient(circle at 30% 30%, #b4ff3b 0%, #4cd137 40%, #1f9d1f 75%, #0a5a0a 100%)",
                                        boxShadow: (!hasBet || interactionsLocked)
                                            ? "none"
                                            : "0 4px 15px rgba(76, 209, 55, 0.6)"
                                    }}
                                    onClick={() => {
                                        if (isFreeSpinMode && !hasBet && hasPreviousBet) {
                                            applyPreparedBet(lastBet.bets, lastBet.coin);
                                            handleSpin(true, true, lastBet.bets);
                                        } else {
                                            handleSpin(false);
                                        }
                                    }}
                                    disabled={interactionsLocked || (!hasBet && !isFreeSpinMode && !hasPreviousBet)}
                                >
                                    {spinning ? 'â€¦' : (isFreeSpinMode && !hasBet && hasPreviousBet) ? 'Rebet & Spin Free' : isFreeSpinMode ? 'Spin Free' : 'Spin'}
                                </button>
                            ) : (
                                <button
                                    id="tour-spin-btn"
                                    className="sw-btn sw-btn--rebet"
                                    style={{ opacity: interactionsLocked ? 0.5 : 1 }}
                                    onClick={handleRebet}
                                    disabled={interactionsLocked}
                                >
                                    Rebet
                                </button>
                            )}

                            <div
                                id="tour-coin-picker"
                                className="sw-coin-wrap"
                                onClick={() => {
                                    if (interactionsLocked) return;
                                    setShowCoinPicker(v => !v);
                                }}
                            >
                                <CasinoChip value={selectedCoinData.value} color={selectedCoinData.color} stripe={selectedCoinData.stripe} size={42} />
                            </div>

                            {/* Free Spin Button â€“ premium casino chip style */}
                            {(() => {
                                const totalAvailable = freeSpinsLeft;
                                const disabled = interactionsLocked || totalAvailable === 0;
                                if (totalAvailable === 0) return null;

                                return (
                                    <button
                                        className="sw-btn"
                                        style={{
                                            position: 'relative',
                                            overflow: 'hidden',
                                            padding: '0',
                                            width: '52px',
                                            height: '52px',
                                            borderRadius: '50%',
                                            border: disabled
                                                ? '3px solid rgba(150,150,150,0.4)'
                                                : '3px solid #ffe066',
                                            background: disabled
                                                ? 'radial-gradient(circle at 35% 32%, #6a6a6a, #3a3a3a 60%, #222)'
                                                : 'radial-gradient(circle at 35% 32%, #ffe566 0%, #f0b429 40%, #b07d00 75%, #7a5500 100%)',
                                            boxShadow: disabled
                                                ? 'none'
                                                : isFreeSpinMode
                                                    ? '0 0 22px 6px rgba(76, 209, 55, 0.85), 0 4px 12px rgba(0,0,0,0.6)'
                                                    : '0 0 14px 4px rgba(240,180,41,0.55), 0 4px 10px rgba(0,0,0,0.5)',
                                            opacity: disabled ? 0.45 : 1,
                                            cursor: disabled ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '1px',
                                            transition: 'box-shadow 0.2s, opacity 0.2s',
                                        }}
                                        onClick={handleFreeSpinClick}
                                        disabled={disabled}
                                        title={totalAvailable > 0 ? `${totalAvailable} free spin(s) available` : 'No free spins left'}
                                    >
                                        {/* Shine overlay */}
                                        {!disabled && (
                                            <span style={{
                                                position: 'absolute', top: '4px', left: '8px',
                                                width: '36px', height: '20px',
                                                borderRadius: '50%',
                                                background: 'rgba(255,255,255,0.28)',
                                                transform: 'rotate(-20deg)',
                                                pointerEvents: 'none',
                                            }} />
                                        )}
                                        <span style={{ fontSize: '15px', lineHeight: 1 }}>ðŸŽ°</span>
                                        <span style={{
                                            fontSize: '9px', fontWeight: 800,
                                            color: disabled ? '#aaa' : '#fff',
                                            textShadow: disabled ? 'none' : '0 1px 2px rgba(0,0,0,0.7)',
                                            letterSpacing: '0.3px',
                                        }}>FREE</span>
                                        <span style={{
                                            fontSize: '9px', fontWeight: 700,
                                            color: disabled ? '#888' : '#3a1a00',
                                            background: disabled ? 'rgba(100,100,100,0.4)' : 'rgba(255,255,255,0.4)',
                                            borderRadius: '8px',
                                            padding: '0px 5px',
                                        }}>{totalAvailable}</span>
                                    </button>
                                );
                            })()}

                            <button
                                className="sw-btn sw-btn--close"
                                style={{ opacity: (!hasBet || interactionsLocked) ? 0.4 : 1 }}
                                onClick={handleCancel}
                                disabled={interactionsLocked || !hasBet}
                            >
                                âœ•
                            </button>
                        </div>
                    </div>

                    {/* Bet row */}
                    <div className="sw-bet-row">
                        {[wheelConfig.leftBets, wheelConfig.rightBets].map((group, gi) => (
                            <div key={gi} className={`sw-bet-group${gi === 1 ? ' sw-bet-group--right' : ''}`}>
                                {group.map(b => {
                                    const displayBets = lockedBets || bets;
                                    const box = displayBets[b.label] || {};
                                    const total = Object.entries(box).reduce((sum, [coin, count]) => {
                                        return sum + Number(coin) * count;
                                    }, 0);
                                    const chips = Object.entries(box).flatMap(([coin, count]) =>
                                        Array.from({ length: count }).map(() => Number(coin))
                                    );

                                    const visibleChips = chips.slice(0, 6);

                                    return (
                                        <div key={b.label} className="sw-bet-slot" id={b.label === '2X' ? 'tour-bet-tray' : undefined}>

                                            {/* âœ… CHIP STACK */}
                                            {chips.length > 0 && (
                                                <div className="sw-chip-stack">
                                                    {visibleChips.map((coinValue, i) => {
                                                        const coinData = COINS.find(c => c.value === coinValue);

                                                        return (
                                                            <div
                                                                key={coinValue + '-' + i}
                                                                className="sw-chip-stack-item"
                                                                style={{ top: `${-i * 5}px`, zIndex: i }}
                                                            >
                                                                <CasinoChip
                                                                    value={coinData.value}
                                                                    color={coinData.color}
                                                                    stripe={coinData.stripe}
                                                                    size={40}
                                                                />
                                                            </div>
                                                        );
                                                    })}

                                                    {/* + more indicator */}
                                                    {chips.length > 6 && (
                                                        <div
                                                            className="sw-chip-stack-more"
                                                            style={{ top: `${-(6 * 5) - 14}px` }}
                                                        >
                                                            +{chips.length - 6}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* âœ… BET BOX */}
                                            <div
                                                className="sw-bet-tray"
                                                onClick={() => handleBetClick(b.label)}
                                                style={{
                                                    borderColor: b.dark,
                                                    cursor: interactionsLocked ? 'default' : 'pointer',
                                                    boxShadow: chips.length > 0
                                                        ? `0 0 0 2px white, 0 7px 0 ${b.dark}, 0 10px 12px rgba(0,0,0,0.5)`
                                                        : `0 7px 0 ${b.dark}, 0 10px 12px rgba(0,0,0,0.45)`,
                                                }}
                                            >
                                                <div className="sw-bet-face" style={{ background: b.grad }}>
                                                    <span className="sw-bet-label">{b.label}</span>
                                                    <div className="sw-bet-sheen" />
                                                </div>

                                                {/* âœ… TOTAL VALUE DISPLAY */}
                                                <div className="sw-bet-val-wrap">
                                                    <div className="sw-bet-val">
                                                        {total > 0 ? total : '0'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    {/* Bottom bar */}
                    <div className="sw-bottom-bar">
                        <span className="sw-bar-item">
                            <b className="sw-bar-key">Bet ID</b>
                            <span className="sw-bar-cyan">&nbsp;54384230108900</span>
                        </span>
                        <span className="sw-bar-item">
                            {isAutoplayActive ? (
                                <>
                                    <b className="sw-bar-key">Autoplay</b>
                                    <span className="sw-bar-cyan">&nbsp;{autoplayCount}</span>
                                </>
                            ) : (() => {
                                const totalAvailable = freeSpinsLeft;
                                return (
                                    <>
                                        <b className="sw-bar-key">Free Spins</b>
                                        <span style={{
                                            color: totalAvailable > 0 ? '#4cd137' : '#ff6b6b',
                                            fontWeight: 700,
                                            marginLeft: 4,
                                        }}>
                                            &nbsp;{totalAvailable}
                                        </span>
                                    </>
                                );
                            })()}
                        </span>
                        <span className="sw-bar-item">
                            <b className="sw-bar-key">Commission</b>
                            <span className="sw-bar-cyan">&nbsp;{Number(gameSettings?.admin_commission || 0)}%</span>
                        </span>
                        <span className="sw-bar-item">
                            <b className="sw-bar-key">Total Stake</b>
                            <span className="sw-bar-cyan">&nbsp;{totalStake.toFixed(2)}</span>
                        </span>
                    </div>

                    <InfoDrawer
                        isOpen={showInfoDrawer}
                        onClose={() => setShowInfoDrawer(false)}
                        selectedSpeed={selectedSpeed}
                        onSpeedChange={setSelectedSpeed}
                        isSoundEnabled={isSoundEnabled}
                        onSoundChange={setIsSoundEnabled}
                        onShowTutorial={() => {
                            setShowInfoDrawer(false);
                            setIsTourOpen(true);
                        }}
                    />

                    <div className={`sw-bottom-drawer-shell${isDrawerOpen ? ' is-open' : ''}`} aria-hidden={!isDrawerOpen}>
                        <div className="sw-bottom-drawer-overlay" onClick={() => setIsDrawerOpen(false)} />
                        <div id="sw-bottom-drawer" className="sw-bottom-drawer" role="dialog" aria-label="Quick actions">
                            <div className="sw-bottom-drawer-actions">
                                <button
                                    type="button"
                                    className="sw-bottom-drawer-btn"
                                    onClick={() => {
                                        if (isFreeSpinMode) {
                                            applyPreparedBet(lastBet.bets, lastBet.coin);
                                            handleSpin(true, true, lastBet.bets);
                                            setIsDrawerOpen(false);
                                        } else {
                                            handleRebet();
                                        }
                                    }}
                                    disabled={!hasPreviousBet || spinning}
                                >
                                    {isFreeSpinMode ? 'Rebet & Spin Free' : 'Same Bet'}
                                </button>
                                <button
                                    type="button"
                                    className="sw-bottom-drawer-btn"
                                    onClick={handleDoubleBet}
                                    disabled={!hasPreviousBet || spinning}
                                >
                                    Double
                                </button>
                                <button
                                    type="button"
                                    className="sw-bottom-drawer-btn sw-bottom-drawer-btn--active"
                                    onClick={handleOpenAutoplayModal}
                                    disabled={isAutoplayActive}
                                // onClick={() => {
                                //   setAlertMsg("ðŸš§ Autoplay coming soon!");
                                //   setTimeout(() => setAlertMsg(null), 2500);
                                // }}
                                >
                                    Autoplay
                                </button>
                            </div>
                        </div>
                    </div>

                    {isAutoplayModalOpen ? (
                        <div className="sw-autoplay-modal-overlay" onClick={() => setIsAutoplayModalOpen(false)}>
                            <div
                                className="sw-autoplay-modal-box"
                                role="dialog"
                                aria-modal="true"
                                aria-label="Autoplay"
                                onClick={(event) => event.stopPropagation()}
                            >
                                <div className="sw-autoplay-modal-title">Autoplay</div>
                                <div className="sw-autoplay-modal-subtitle">Selecting will start Autoplay</div>
                                <div className="sw-autoplay-modal-grid">
                                    {AUTOPLAY_OPTIONS.map((option) => (
                                        <button
                                            key={option}
                                            type="button"
                                            className="sw-autoplay-modal-btn"
                                            onClick={() => handleAutoplayOptionSelect(option)}
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {/* Flash overlay */}
                    {flash && <div className="sw-flash" />}

                    {/* Alert toast */}
                    {alertMsg && (
                        <div className={`sw-alert-toast${alertMsg.toLowerCase().includes('active') ? ' sw-alert-toast--success' : ''}`}>
                            <span>{alertMsg.toLowerCase().includes('active') ? 'âœ…' : 'âš ï¸'}</span>
                            {alertMsg}
                        </div>
                    )}

                    {/* Win / Lose modal */}
                    {winModal && (
                        <ResultModal
                              variant="multiplier"
                              open={Boolean(winModal)}
                            title={winModal.win ? (winModal.title || 'Reward Achieved!') : (winModal.title || 'Better Luck!')}
                            multiplier={typeof winModal.amount === 'number' ? (winModal.win ? `+${winModal.amount.toFixed(2)}` : `-${winModal.amount.toFixed(2)}`) : (winModal.label || '0x')}
                            subtitle={winModal.subtitle ?? (winModal.win ? 'This reward has been added to your balance.' : 'try again next round')}
                            subtitle2="Increase your prediction amount next round to maximize your potential reward."
                            buttonLabel={winModal.autoClose ? 'Close' : (winModal.continueText || 'Next')}
                            onClose={winModal.autoClose ? handleResultModalClose : handleResultContinue}
                        />
                    )}

                    {showJackpotWheel && (
                        <JackpotWheel
                            jackpotPool={jackpotPool}
                            tickets={jackpotTickets}
                            segments={jackpotSegments}
                            onResult={async (res) => {
                                console.log('Jackpot result received:', res);
                                if (res.amount > 0) {
                                    setBalance(prev => prev + res.amount);
                                }
                                // Call payout API to persist the win/loss and decrement ticket
                                try {
                                    await Axios.post('/spinWheel/jackpot-payout', {
                                        amount: res.amount,
                                        label: res.label,
                                        pct: res.pct
                                    });
                                } catch (err) {
                                    console.error('Failed to process jackpot payout:', err);
                                }
                            }}
                            onClose={() => {
                                setShowJackpotWheel(false);
                                setIsResultPending(false);
                                getWallet();
                            }}
                        />
                    )}

                    <TourModule
                        isOpen={isTourOpen}
                        onClose={() => setIsTourOpen(false)}
                        tutorialStatus={wallet?.spinAndWinTutorialStatus}
                        onUpdateStatus={updateTutorialStatus}
                    />
                </div>
            </div>
            </div>
        </div>
    );
}

